import React, {
    ReactChild,
    ReactElement,
    useContext,
    useMemo,
    useState,
} from 'react';
import { useRouter } from 'next/router';
import getPersonOverview, {
    editPersonOverviewData,
    getPersonOverviewEditFields,
    onSuccessEditPersonOverview,
} from './utils/getPersonOverview';
import AppLayout from 'components/layouts/AppLayout';
import { PersonBaseInterface } from 'components/interfaces/PersonInterface';
import { LinkInterface } from 'components/interfaces/GeneralInterface';
import { ActionMapInterface } from 'components/pages/base/topControlBlock/TopControlBlock';
import { getLinks } from 'components/pages/person/detail/base/PersonLinks';
import { getPersonTags } from 'components/pages/person/detail/base/PersonTags';
import { getActionsMapPersonProfilePage } from 'components/pages/person/detail/base/Commonts';
import { GlobalContext } from 'pages/_app';
import { useTranslation } from 'utils/localization';
import { UserService } from 'services/user.service';
import { ConfigPageInterface } from 'utils/helpers/server/serverSideProps/config';
import {
    Config_PersonFields_ApiInterface,
    Config_PersonDetail_ApiInterface,
    Config_PlayerDetail_ApiInterface,
    Config_PlayerContracts_ApiInterface,
    Config_PlayerContract_ApiInterface,
    Config_PlayerMatches_ApiInterface,
    Config_RefereeDetail_ApiInterface,
    Config_RefereeMatches_ApiInterface,
    Config_RefereeLicences_ApiInterface,
    Config_DelegateDetail_ApiInterface,
    Config_DelegateMatches_ApiInterface,
    Config_DelegateLicences_ApiInterface,
    Config_InternalUserDetail_ApiInterface,
    Config_PersonApplications_ApiInterface,
    Config_PlayerContractCreate_ApiInterface,
    Config_PersonAccountTransactions_ApiInterface,
    Config_PersonAccountRewardsDocuments_ApiInterface,
    Config_PersonAccountRewardDocument_ApiInterface,
    Config_PersonAccountIssuedDocument_ApiInterface,
    Config_PersonBillingFields_ApiInterface,
    Config_PersonAbsences_ApiInterface,
    Config_AgentsDetail_ApiInterface,
    Config_AgentsContractsSearch_ApiInterface,
    Config_AgentsInsurancesSearch_ApiInterface,
    Config_AgentsContractDetail_ApiInterface,
    Config_AgentsInsuranceDetail_ApiInterface,
    Config_AgentsContractNew_ApiInterface,
    Config_AgentsInsuranceNew_ApiInterface,
    Config_PersonAccountIssuedDocuments_ApiInterface,
    TagApiInterface,
    Config_PersonAuditLog_ApiInterface,
    FootballStateEnum,
    FutsalStateEnum,
} from 'submodules/api_middleware';
import GenerateEnvelopeModal from 'components/pages/person/detail/base/modal/GenerateEnvelopeModal';
import { Config_CoachDetail_ApiInterface } from 'submodules/api_middleware/src/interfaces/config/CoachDetailConfigApiInterface';
import { Config_CoachLicences_ApiInterface } from 'submodules/api_middleware/src/interfaces/config/CoachLicencesConfigApiInterface';
import { Config_CoachMatches_ApiInterface } from 'submodules/api_middleware/src/interfaces/config/CoachMatchesConfigApiInterface';
import { connectStrings } from 'utils/helpers/browser/string';
import { handlePhotoResponse } from 'utils/helpers/browser/photo';
import { OverviewBlockType } from 'components/layouts/overview/Overview';

interface Props
    extends ConfigPageInterface<
        | Config_PersonFields_ApiInterface
        | Config_PersonDetail_ApiInterface
        | Config_PlayerDetail_ApiInterface
        | Config_PlayerContracts_ApiInterface
        | Config_PlayerContract_ApiInterface
        | Config_PlayerMatches_ApiInterface
        | Config_RefereeDetail_ApiInterface
        | Config_RefereeMatches_ApiInterface
        | Config_RefereeLicences_ApiInterface
        | Config_DelegateDetail_ApiInterface
        | Config_DelegateMatches_ApiInterface
        | Config_DelegateLicences_ApiInterface
        | Config_InternalUserDetail_ApiInterface
        | Config_PersonApplications_ApiInterface
        | Config_PersonAccountTransactions_ApiInterface
        | Config_PlayerContractCreate_ApiInterface
        | Config_PersonAccountRewardsDocuments_ApiInterface
        | Config_PersonAccountRewardDocument_ApiInterface
        | Config_PersonAccountIssuedDocument_ApiInterface
        | Config_PersonBillingFields_ApiInterface
        | Config_PersonAbsences_ApiInterface
        | Config_PersonAuditLog_ApiInterface
        | Config_AgentsDetail_ApiInterface
        | Config_AgentsContractsSearch_ApiInterface
        | Config_AgentsInsurancesSearch_ApiInterface
        | Config_AgentsContractDetail_ApiInterface
        | Config_AgentsInsuranceDetail_ApiInterface
        | Config_AgentsContractNew_ApiInterface
        | Config_AgentsInsuranceNew_ApiInterface
        | Config_CoachDetail_ApiInterface
        | Config_PersonAccountIssuedDocuments_ApiInterface
        | Config_CoachLicences_ApiInterface
        | Config_CoachMatches_ApiInterface
    > {
    children: ReactChild;
    pageSubTitle?: string;
    minimizeGradient?: boolean;
    biggestGradient?: boolean;
    topActionsMap?: ActionMapInterface;
    personBase: PersonBaseInterface;
    customTags?: TagApiInterface[];
    hideOtherTags?: boolean;
    withEditActions?: boolean;
    hideOverview?: boolean;
    overview?: OverviewBlockType;
    isArchived?: boolean;
}

const PersonBase = ({
    config,
    children,
    pageSubTitle,
    topActionsMap,
    customTags = [],
    hideOtherTags = false,
    hideOverview,
    minimizeGradient,
    biggestGradient,
    personBase: {
        profileColors,
        person,
        member,
        player,
        playerIndices,
        memberIndices,
        memberStatus,
    },
    withEditActions,
    overview,
    isArchived,
}: Props): ReactElement => {
    const { t } = useTranslation();
    const { user, viewerRoleFlags } = useContext(GlobalContext);
    const [personPhoto, setPersonPhoto] = useState<string>(person.photo);
    const [printEnvelopeModalIsOpen, setPrintEnvelopeModal] = useState(false);
    const Router = useRouter();
    const personLinksFlags = useMemo(
        () => ({
            isMe: !!user && user.person_id === person.id,
            viewerIsRefOrDel:
                viewerRoleFlags.isReferee || viewerRoleFlags.isDelegate,
        }),
        [user, person.id, viewerRoleFlags]
    );
    const links = useMemo<LinkInterface[]>(
        (): LinkInterface[] =>
            getLinks(
                person.id,
                config.pageMenu,
                person.agent_id,
                person.is1_publish_id,
                personLinksFlags
            ),
        [Router, person, config, personLinksFlags]
    );
    const pageTitleBase = connectStrings(
        ' ',
        person.first_name,
        person.last_name
    );

    const addPhotoToPerson = async (photo: string) => {
        if (photo) {
            const responsePhoto = await new UserService().uploadPhoto({
                photo,
                id: Number(person.id),
            });
            handlePhotoResponse(responsePhoto, setPersonPhoto);
        }
    };

    const internalTags =
        !hideOtherTags && config.page.modules.indications?.enabled
            ? getPersonTags({ t, person, member, memberStatus })
            : customTags || [];

    const tags =
        !hideOtherTags && config.page.modules.indications?.enabled
            ? memberIndices
            : [];

    const isPersonArchived = [
        FootballStateEnum.ARCHIVE,
        FutsalStateEnum.ARCHIVE,
    ].includes(memberStatus);

    return (
        <AppLayout
            title={
                pageSubTitle
                    ? `${pageTitleBase} · ${pageSubTitle}`
                    : pageTitleBase
            }
            pageTitle={{
                primaryNormalText: person.first_name,
                primaryBoldText: person.last_name,
                secondaryText: pageSubTitle
                    ? pageSubTitle
                    : String(player?.registered_in?.id) || '',
            }}
            colors={profileColors}
            withBreadcrumbs
            showGradient
            minimizeGradient={minimizeGradient || !playerIndices}
            biggestGradient={biggestGradient}
            configActions={config.page.actions}
            topActionsMap={
                topActionsMap
                    ? topActionsMap
                    : getActionsMapPersonProfilePage(person.id, () =>
                          setPrintEnvelopeModal(true)
                      )
            }
            isArchived={isPersonArchived || isArchived}
            overview={{
                tags: { internalTags, tags },
                blocks:
                    !hideOverview &&
                    config.page.modules.overview?.enabled &&
                    overview
                        ? overview
                        : {
                              filterOutUnset: true,
                              fields: getPersonOverview(
                                  t,
                                  person,
                                  member,
                                  playerIndices,
                                  player
                              ),
                              whiteActions: true,
                              isEditable:
                                  config.page.modules.overview?.editable,
                              onEdit: withEditActions && {
                                  fields: getPersonOverviewEditFields(
                                      t,
                                      person
                                  ),
                                  submit: (values) =>
                                      editPersonOverviewData(person.id, values),
                                  onSuccess: onSuccessEditPersonOverview,
                              },
                          },
            }}
            innerPageMenu={{
                imageUrl: personPhoto,
                links: links,
                marginTop:
                    internalTags.length === 0 && memberIndices?.length === 0,
                //@ts-ignore-next-line
                editPhotoCb: config.page.actions.other['upload_photo']?.enabled
                    ? (photo) => addPhotoToPerson(photo)
                    : null,
                helpKey: 'photoUploader.memberHelp',
            }}
        >
            <>
                {printEnvelopeModalIsOpen && (
                    <GenerateEnvelopeModal
                        show={printEnvelopeModalIsOpen}
                        hideCb={() => setPrintEnvelopeModal(false)}
                        person={person}
                        player={player}
                    />
                )}
                <div>{children}</div>
            </>
        </AppLayout>
    );
};

export default PersonBase;
